import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import type { ReactNode } from 'react'
import type { SendCounts } from '@/services/notificationService'

// Text of the Figma confirm frame, bullet by bullet.
const NOTICE_BULLETS = [
  'ההודעה תישלח באמצעות הערוץ שנבחר (SMS / WhatsApp / Email) בהתאם לפרטי הקשר המעודכנים במערכת.',
  'תוכן ההודעה כולל נתונים דינמיים (כגון שם הצרכן, כתובת, מספר מד המים ופרטי ההתראה), ולכן אורכה הסופי עשוי להשתנות.',
  'בשל אורך ההודעה, הודעות SMS עשויות להישלח כמספר הודעות בהתאם למדיניות ספק התקשורת.',
  'אם פרטי הקשר אינם קיימים או אינם תקינים, ייתכן שההודעה לא תישלח.',
  'מסירת ההודעה תלויה בספק התקשורת או בשירות הדואר האלקטרוני, ולכן ייתכנו עיכובים או כשלי מסירה.',
  'לאחר אישור הפעולה לא ניתן לבטל את השליחה.',
  'פעולת השליחה תתועד בהיסטוריית ההתראות של מד המים.',
]

type Props = {
  counts: SendCounts | null
  // a send from a table counts premises, which can be fewer than the consumers listed
  tableAudience?: boolean
  warning?: ReactNode
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}

const ConfirmSendDialog = ({
  counts,
  tableAudience = false,
  warning,
  busy,
  error,
  onConfirm,
  onCancel,
}: Props) => {
  const zeroResolved = counts?.premisesResolved === 0
  const zeroEnqueued =
    !!counts &&
    counts.premisesResolved > 0 &&
    counts.notificationsEnqueued === 0
  return (
    <Dialog
      open={counts !== null}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby="confirm-send-title"
    >
      <DialogTitle
        id="confirm-send-title"
        sx={{
          direction: 'rtl',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: 16,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <InfoOutlinedIcon fontSize="small" color="success" />
        יש לשים לב
      </DialogTitle>
      <DialogContent sx={{ direction: 'rtl' }}>
        {warning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {warning}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="ul" sx={{ pr: 3, pl: 0, my: 2, fontSize: 14 }}>
          {NOTICE_BULLETS.map(text => (
            <li key={text}>{text}</li>
          ))}
        </Box>
        {counts && (
          <Box sx={{ mb: 2 }}>
            {/* counts ALWAYS render (spec §5: no mystery zeros) */}
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              {`${counts.premisesResolved} נכסים נמצאו · ${counts.premisesWithoutTargets} ללא פרטי התקשרות · ${counts.notificationsEnqueued} הודעות יישלחו`}
            </Typography>
            {!!counts.premisesOutsideSite && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {`${counts.premisesOutsideSite} נכסים מאתר אחר לא נכללו בשליחה`}
              </Typography>
            )}
            {tableAudience && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                הספירה היא של נכסים. נכס שיש בו כמה צרכנים נספר פעם אחת, ולכן
                מספר הנכסים יכול להיות קטן ממספר הצרכנים בטבלה.
              </Typography>
            )}
            {zeroResolved && <Typography>לא נמצאו נמענים</Typography>}
            {zeroEnqueued && (
              <Typography>נמצאו נכסים אך ללא יעדי שליחה</Typography>
            )}
          </Box>
        )}
        <Typography sx={{ fontWeight: 700, textAlign: 'center' }}>
          לשלוח את ההודעה?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy || zeroResolved || zeroEnqueued}
        >
          אישור
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={busy}>
          ביטול
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmSendDialog
